/**
 * KPI 总览 Tab「UA-ROI Campaign & UA Media Channel」下的筛选收敛：
 * custom_filters 中 campaign 必须为 UA-ROI 类型，ua_network 必须为 UA 类型；
 * 与用户筛选求交集；255 表示该维度下 UA-ROI / UA 的全部 code。
 */

export type TKpiOverviewDataScope = 'all' | 'ua_roi_ua_media';

type TFilterVal = Record<string, unknown>;

type TSelectorItem = Record<string, unknown>;
type TSelectorData = Record<string, TSelectorItem[]>;

interface ICustomFilter {
  key: string;
  filter_list: string[];
}

const getUserFilterValues = (
  filterVal: TFilterVal | undefined,
  filterKey: string,
): string[] => {
  if (filterKey === 'market') {
    const region = filterVal?.region as { markets?: unknown; } | undefined;
    const markets = region?.markets;
    return Array.isArray(markets) ? (markets as string[]) : [];
  }
  const val = filterVal?.[filterKey];
  if (!val) return [];
  if (Array.isArray(val)) return val as string[];
  if (typeof val === 'object') {
    const childVal = (val as Record<string, unknown>)[filterKey];
    if (Array.isArray(childVal)) return childVal as string[];
  }
  return [];
};

const codesBySelectorMatchType = (
  selector: TSelectorData | undefined,
  selectorKey: string,
  matchType: string,
): string[] => {
  if (!selector) return [];
  const list = selector[selectorKey] || [];
  return list
    .filter(item => item?.type === matchType)
    .map(item => String(item?.code ?? '').trim())
    .filter(Boolean);
};

/**
 * 与用户选择求交集；用户为 255/空视为使用该维度下允许列表全部。
 * 交集为空返回 null（不应请求）。
 */
export const intersectUserWithAllowList = (
  userValues: string[],
  allowList: string[],
): string[] | null => {
  if (allowList.length === 0) return null;
  if (
    userValues.length === 0 ||
    (userValues.length === 1 && String(userValues[0]) === '255')
  ) {
    return allowList;
  }
  const allowSet = new Set(allowList);
  const hit = userValues.filter(v => allowSet.has(String(v)));
  return hit.length > 0 ? hit : null;
};

/**
 * 将 UA-ROI + UA Media 口径合并进已 format 后的 filters（含 custom_filters）。
 * 不满足交集时返回 false，调用方应跳过请求并展示无数据。
 */
export const mergeUaRoiUaMediaScopeIntoFormattedFilters = (
  formatted: Record<string, unknown>,
  filterVal: TFilterVal | undefined,
  selector: TSelectorData | undefined,
): Record<string, unknown> | false => {
  const uaRoiCampaignCodes = codesBySelectorMatchType(
    selector,
    'campaign',
    'UA-ROI',
  );
  const uaMediaCodes = codesBySelectorMatchType(
    selector,
    'ua_network',
    'UA',
  );

  const userCampaign = getUserFilterValues(filterVal, 'campaign');
  const userUa = getUserFilterValues(filterVal, 'ua_network');

  const campaignList = intersectUserWithAllowList(
    userCampaign,
    uaRoiCampaignCodes,
  );
  const uaList = intersectUserWithAllowList(userUa, uaMediaCodes);

  if (campaignList === null || uaList === null) {
    return false;
  }

  const next: Record<string, unknown> = { ...formatted };
  const customFilters: ICustomFilter[] = Array.isArray(next.custom_filters)
    ? [...(next.custom_filters as ICustomFilter[])]
    : [];

  const upsert = (key: string, filter_list: string[]) => {
    const idx = customFilters.findIndex(c => c.key === key);
    if (idx >= 0) {
      customFilters[idx] = { key, filter_list };
    } else {
      customFilters.push({ key, filter_list });
    }
  };

  upsert('campaign', campaignList);
  upsert('ua_network', uaList);
  next.custom_filters = customFilters;

  return next;
};

/**
 * UA 口径下用于 React key：在底层 Service 未随 beforeRequest 更新闭包时，
 * 通过 key 变化强制重新挂载，使 metrics_trend 使用最新的 selector 合并 custom_filters。
 */
export const getUaRoiUaMediaScopeSelectorFingerprint = (
  selector: TSelectorData | undefined,
): string => {
  const c = codesBySelectorMatchType(selector, 'campaign', 'UA-ROI')
    .slice()
    .sort()
    .join(',');
  const u = codesBySelectorMatchType(selector, 'ua_network', 'UA')
    .slice()
    .sort()
    .join(',');
  return `${c}::${u}`;
};

/**
 * 判断 UA-ROI + UA Media 口径下筛选器是否无数据（交集为空）。
 * 用于业务层短路：不请求接口、直接展示无数据。
 */
export const isUaRoiUaMediaScopeNoData = (
  filterVal: TFilterVal | undefined,
  selector: TSelectorData | undefined,
): boolean => {
  const uaRoiCampaignCodes = codesBySelectorMatchType(selector, 'campaign', 'UA-ROI');
  const uaMediaCodes = codesBySelectorMatchType(selector, 'ua_network', 'UA');

  const userCampaign = getUserFilterValues(filterVal, 'campaign');
  const userUa = getUserFilterValues(filterVal, 'ua_network');

  const campaignList = intersectUserWithAllowList(userCampaign, uaRoiCampaignCodes);
  const uaList = intersectUserWithAllowList(userUa, uaMediaCodes);

  return campaignList === null || uaList === null;
};
